import { getTrackedShopUrl, SHOP_BASE_URL } from '../../../lib/external-links'
import { SHOP_COLLECTION_HANDLES, getShopCollectionPath } from '../../../lib/shopify-catalog'

function getShopifyHandle(pathOrUrl) {
  const url = pathOrUrl.startsWith('http')
    ? new URL(pathOrUrl)
    : new URL(pathOrUrl, SHOP_BASE_URL)
  const segments = url.pathname.split('/').filter(Boolean)
  const productIndex = segments.indexOf('products')

  if (productIndex === -1) {
    return null
  }

  return segments[productIndex + 1] || null
}

function createShopProduct({ shopPath, utmContent, ...product }) {
  return {
    ...product,
    href: getTrackedShopUrl(shopPath, {
      utm_campaign: 'help_me_choose',
      utm_content: utmContent,
    }),
    shopifyHandle: getShopifyHandle(shopPath),
  }
}

function createBrowseLink(path, utmContent, label) {
  return {
    href: getTrackedShopUrl(path, {
      utm_campaign: 'help_me_choose',
      utm_content: utmContent,
    }),
    label,
  }
}

export const PRODUCT_LIBRARY = {
  assessmentLead: {
    title: 'Water Doctor Home Assessment',
    eyebrow: 'Primary recommendation',
    category: 'Expert-guided fit confirmation',
    href: '/forms/home-assessment',
    internal: true,
    cta: 'Submit Home Assessment',
    highlights: [
      'Best when the symptom picture is still mixed and you do not want to guess between categories.',
      'Lets Water Doctor confirm water source, likely treatment path, sizing, and install fit before you buy.',
      'A stronger starting point than forcing a product choice when the real issue is still unclear.',
    ],
  },
  wdsvtt: createShopProduct({
    title: 'Water Doctor Chlorine Removal Vertical Twin Tank Water Softener',
    eyebrow: 'Primary recommendation',
    category: 'Whole-home softening + chlorine removal',
    shopPath: '/products/water-doctor-wdsvtt-chlorine-removal-twin-tank-water-softener',
    utmContent: 'wdsvtt',
    browseKey: 'whole-house-chlorine-filters',
    cta: 'View WD SVTT',
    highlights: [
      'Best fit for city-water homes dealing with hard water and chlorine at the same time.',
      'The carbon tank is separate from the softener section, so carbon media can be replaced on its own schedule.',
      'That matters because carbon often declines around 5 to 7 years, while the softener side can last much longer.',
    ],
  }),
  ultimateCombo: createShopProduct({
    title: 'Water Doctor Ultimate Water Softener & Conditioner',
    eyebrow: 'Backup option',
    category: 'Whole-home softening + chlorine removal',
    shopPath: '/products/cdshtt-twin-tank-water-conditioner',
    utmContent: 'ultimate_combo',
    browseKey: 'whole-house-chlorine-filters',
    cta: 'View Ultimate System',
    highlights: [
      'Built for homes that need both scale reduction and chlorine treatment in a sized system.',
      'Available in 30K, 45K, and 60K capacities so the fit can be matched to household demand.',
      'Useful when you want the scale-plus-chlorine path but need more sizing flexibility than WD SVTT.',
    ],
  }),
  symphonyStandard: createShopProduct({
    title: 'Water Doctor Scale-Shield Single-Tank Water Softener',
    eyebrow: 'Primary recommendation',
    category: 'Whole-home hard-water protection',
    shopPath: '/products/cds-symphony-water-softener',
    utmContent: 'symphony_softener',
    browseKey: 'water-softeners',
    cta: 'View Scale-Shield',
    highlights: [
      'This is the standard residential softener path when hard water is the main issue.',
      'Available in 30K, 45K, and 60K sizes to match different household sizes.',
      'Best when you want true soft water and chlorine is not the main problem.',
    ],
  }),
  symphonyLite: createShopProduct({
    title: 'Water Doctor Scale-Shield All-in-One Water Softener',
    eyebrow: 'Backup option',
    category: 'Whole-home hard-water protection',
    shopPath: '/products/symphony-lite-all-in-one-softener',
    utmContent: 'symphony_lite',
    browseKey: 'water-softeners',
    cta: 'View All-in-One Softener',
    highlights: [
      'This is the one-piece softener option for buyers who want a more integrated format.',
      'Still aimed at hard-water correction, not chlorine removal.',
      'A good backup when you want a simpler all-in-one softener presentation.',
    ],
  }),
  carbonFilter: createShopProduct({
    title: 'Water Doctor Clari-Taste Whole-House Carbon Filter',
    eyebrow: 'Primary recommendation',
    category: 'Whole-home chlorine and odour removal',
    shopPath:
      '/products/water-doctor-ccf-carbon-filter-whole-house-carbon-filtration-system-salt-free',
    utmContent: 'carbon_filter',
    browseKey: 'whole-house-chlorine-filters',
    cta: 'View Carbon Filter',
    highlights: [
      'Best when chlorine taste, smell, and shower comfort are the problem, but hardness is not.',
      'A simpler whole-home city-water path than buying a softener you may not need.',
      'Good first recommendation when you want every tap treated without adding salt.',
    ],
  }),
  gf3: createShopProduct({
    title: 'Water Doctor GF3 800GPD RO System',
    eyebrow: 'Primary recommendation',
    category: 'High-output drinking-water RO',
    shopPath: '/products/water-doctor-gf3-800gpd-ro-system',
    utmContent: 'gf3_ro',
    browseKey: 'reverse-osmosis-systems',
    cta: 'View GF3 RO',
    highlights: [
      'A strong drinking-water-first recommendation when the issue is mainly taste, contaminants, or kitchen use.',
      'High-output reverse osmosis without turning the whole-home path into a bigger project.',
      'A practical primary fit for most homes that want cleaner water at the point of use.',
    ],
  }),
  comoR1000: createShopProduct({
    title: 'Water Doctor COMO-R1000 Tankless RO System',
    eyebrow: 'Primary recommendation',
    category: 'Premium tankless drinking-water RO',
    shopPath: '/products/como-r1000-tankless-ro-system',
    utmContent: 'como_r1000',
    browseKey: 'reverse-osmosis-systems',
    cta: 'View COMO-R1000',
    highlights: [
      'A stronger fit when you want faster fill speed, a tankless footprint, or heavier daily kitchen use.',
      'Works especially well for busier households that do not want a slower under-sink experience.',
      'A premium backup or primary path when convenience matters as much as filtration quality.',
    ],
  }),
  ros4a: createShopProduct({
    title: 'Water Doctor 4-Stage Undersink RO System',
    eyebrow: 'Backup option',
    category: 'Classic under-sink RO',
    shopPath: '/products/ros4a-4-stage-ro-system',
    utmContent: 'ros4a',
    browseKey: 'reverse-osmosis-systems',
    cta: 'View 4-Stage RO',
    highlights: [
      'A straightforward fallback when you want a more classic under-sink RO format.',
      'Good if premium tankless speed is not necessary and you want a familiar RO setup.',
      'Useful as a backup when budget or simplicity matters more than maximum flow.',
    ],
  }),
  clariTasteIron: createShopProduct({
    title: 'Water Doctor Clari-Taste Whole-Home Iron Filter',
    eyebrow: 'Primary recommendation',
    category: 'Whole-home iron treatment',
    shopPath: '/products/water-doctor-clari-taste',
    utmContent: 'clari_taste_iron',
    browseKey: 'water-softeners',
    cta: 'View Iron Filter',
    highlights: [
      'This is the path to review when iron is driving staining or well-water issues.',
      'Available in multiple sizes so the system can be matched to household demand.',
      'A better first stop than a standard softener when iron is the real problem.',
    ],
  }),
  clariTannin: createShopProduct({
    title: 'Water Doctor Specialty Whole-Home Colour Remover',
    eyebrow: 'Backup option',
    category: 'Well-water tannin treatment',
    shopPath:
      '/products/water-doctor-cdcr-tannin-filter-whole-house-tannin-removal-system-9-48',
    utmContent: 'clari_tannin',
    browseKey: 'water-softeners',
    cta: 'View Tannin Filter',
    highlights: [
      'A better backup when the water is more tea-coloured or yellow-brown than rusty.',
      'Useful when the main issue sounds more like tannins than iron or sulphur.',
      'Often worth confirming by assessment before purchase because well-water chemistry varies a lot.',
    ],
  }),
  ultimateSaltFree: createShopProduct({
    title: 'Water Doctor Ultimate Salt-Free Scale Prevention & Chlorine Removal',
    eyebrow: 'Backup option',
    category: 'Whole-home scale prevention + chlorine removal',
    shopPath: '/products/iohtt-dualflow-chlorine-removal-descaler',
    utmContent: 'ultimate_salt_free',
    browseKey: 'salt-free-water-conditioners',
    cta: 'View Salt-Free Combo',
    highlights: [
      'Designed for households that want chlorine removal plus salt-free scale prevention.',
      'Available in 30K, 45K, and 60K capacities.',
      'Best as a backup when you want to avoid a salt-based softener path.',
    ],
  }),
  uvStandard: createShopProduct({
    title: 'Water Doctor Pure-Guard Whole-Home UV Water Disinfection System',
    eyebrow: 'Primary recommendation',
    category: 'Whole-home UV disinfection',
    shopPath: '/products/uvdynamics-point-of-entry-uv-water-disinfection-system',
    utmContent: 'uv_pure_guard_standard',
    browseKey: 'uv-systems',
    cta: 'View UV System',
    highlights: [
      'The clearest starting point when bacteria or microorganism protection is the top concern.',
      'Fits well-water and safety-first paths better than trying to solve everything with carbon or softening alone.',
      'Should still be matched to flow rate and pre-filtration before purchase.',
    ],
  }),
  uvHighFlow: createShopProduct({
    title: 'Water Doctor Pure-Guard UV System for Higher-Flow Homes',
    eyebrow: 'Backup option',
    category: 'Whole-home UV disinfection',
    shopPath: '/products/uvdynamics-point-of-entry-uv-water-disinfection-system',
    utmContent: 'uv_pure_guard_high_flow',
    browseKey: 'uv-systems',
    cta: 'Review UV Model Options',
    highlights: [
      'Use the same UV platform but step up the model choice when your home has higher peak flow or heavier occupancy.',
      'This is the backup path when the standard model may be undersized for household demand.',
      'The product page covers the platform, but the final model should be confirmed against flow rate.',
    ],
  }),
}

export const BROWSE_LINKS = {
  'water-softeners': createBrowseLink(
    getShopCollectionPath(SHOP_COLLECTION_HANDLES.waterSofteners),
    'browse_water_softener_collection',
    'Browse water softeners',
  ),
  'reverse-osmosis-systems': createBrowseLink(
    getShopCollectionPath(SHOP_COLLECTION_HANDLES.reverseOsmosisSystems),
    'browse_ro_collection',
    'Browse RO systems',
  ),
  'uv-systems': createBrowseLink(
    getShopCollectionPath(SHOP_COLLECTION_HANDLES.uvSystems),
    'browse_uv_collection',
    'Browse UV systems',
  ),
  'salt-free-water-conditioners': createBrowseLink(
    getShopCollectionPath(SHOP_COLLECTION_HANDLES.saltFreeWaterConditioners),
    'browse_salt_free_collection',
    'Browse salt-free conditioners',
  ),
  'whole-house-chlorine-filters': createBrowseLink(
    getShopCollectionPath(SHOP_COLLECTION_HANDLES.wholeHouseChlorineFilters),
    'browse_chlorine_collection',
    'Browse chlorine-removal systems',
  ),
}

export function getHelpMeChooseShopifyHandles() {
  return [...new Set(Object.values(PRODUCT_LIBRARY).map((product) => product.shopifyHandle).filter(Boolean))]
}
